import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { AxiosResponseHeaders } from "axios";
import {
  usePagination,
  UsePaginationOptions,
  UsePaginationReturn,
} from "./usePagination";
import { useCallback, useRef, useState } from "react";

export interface PaginatedApiFunction<TFilter, TData> {
  (filter: TFilter, options?: { headers?: Record<string, string> }): Promise<{
    data: TData;
    headers: AxiosResponseHeaders;
  }>;
}

export interface UsePaginatedQueryOptions<TFilter, TData, TError>
  extends UsePaginationOptions {
  mutationOptions?: Omit<
    UseMutationOptions<
      { data: TData; headers: AxiosResponseHeaders },
      TError,
      { filter: TFilter; rangeHeader: string }
    >,
    "mutationFn"
  >;
  onSuccess?: (data: TData, totalCount: number) => void;
  onError?: (error: TError) => void;
}

export interface UsePaginatedQueryReturn<TFilter, TData, TError>
  extends UsePaginationReturn {
  // Query state
  data: TData | undefined;
  error: TError | null;
  isPending: boolean;

  // Query actions
  executeQuery: (filter: TFilter, page?: number, pageSize?: number) => void;
  refetch: () => void;

  // Filter state
  currentFilter: TFilter | null;
  setCurrentFilter: (filter: TFilter | null) => void;
}

export function usePaginatedQuery<TFilter, TData, TError = Error>(
  apiFunction: PaginatedApiFunction<TFilter, TData>,
  options: UsePaginatedQueryOptions<TFilter, TData, TError> = {}
): UsePaginatedQueryReturn<TFilter, TData, TError> {
  const { mutationOptions, onSuccess, onError, ...paginationOptions } = options;

  const pagination = usePagination(paginationOptions);
  const [currentFilter, setCurrentFilter] = useState<TFilter | null>(null);
  const [previousData, setPreviousData] = useState<TData | undefined>(undefined);

  // Keep currentPage in a ref so executeQuery always has the latest value without needing it as a dep
  const currentPageRef = useRef(pagination.currentPage);
  currentPageRef.current = pagination.currentPage;

  // Track the last used pageSize so page navigation respects it
  const currentPageSizeRef = useRef<number | undefined>(paginationOptions.pageSize);

  const {
    mutate: performQuery,
    error,
    data: queryData,
    isPending,
  } = useMutation({
    ...mutationOptions,
    mutationFn: ({
      filter,
      rangeHeader,
    }: {
      filter: TFilter;
      rangeHeader: string;
    }) => {
      return apiFunction(filter, {
        headers: {
          range: rangeHeader,
        },
      });
    },
    onSuccess: (response) => {
      // Parse the Content-Range header to get total count
      const contentRange =
        response.headers["content-range"] || response.headers["Content-Range"];
      let totalCount = 0;
      if (contentRange) {
        const parsedRange = pagination.parseContentRange(
          contentRange as string
        );
        if (parsedRange) {
          totalCount = parsedRange.total;
          pagination.setTotalCount(totalCount);
        }
      } else {
        // Fallback: if no Content-Range header, use data length as totalCount
        totalCount = Array.isArray(response.data) ? response.data.length : 0;
        pagination.setTotalCount(totalCount);
      }

      // Store the new data as previous data for next query
      setPreviousData(response.data);
      onSuccess?.(response.data, totalCount);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

  const executeQuery = useCallback(
    (filter: TFilter, page?: number, pageSize?: number) => {
      const targetPage = page ?? currentPageRef.current;
      if (pageSize !== undefined) {
        currentPageSizeRef.current = pageSize;
      }
      const rangeHeader = pagination.calculateRange(targetPage, currentPageSizeRef.current);

      setCurrentFilter(filter);
      pagination.setCurrentPage(targetPage);

      performQuery({
        filter,
        rangeHeader,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [performQuery]
  );

  const refetch = useCallback(() => {
    if (currentFilter) {
      executeQuery(currentFilter, pagination.currentPage);
    } else {
      console.warn('usePaginatedQuery: refetch() called before executeQuery — no-op');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter]); // Intentionally excluding executeQuery to avoid infinite loops

  const handlePageChange = useCallback(
    (page: number) => {
      if (currentFilter) {
        executeQuery(currentFilter, page, currentPageSizeRef.current);
      } else {
        pagination.setCurrentPage(page);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter] // Intentionally excluding executeQuery and pagination to avoid infinite loops
  );

  return {
    ...pagination,
    setCurrentPage: handlePageChange,
    data: queryData?.data ?? previousData,
    error,
    isPending,
    executeQuery,
    refetch,
    currentFilter,
    setCurrentFilter,
  };
}
